import React, { FC, ReactNode } from "react";
import { Tag, TagProps } from "@UI/Components/Tag/Tag";
import { Icon } from "@UI/Components/Icon/Icon";
import { SparklesSolid } from "@UI/Components/Icons/SparklesSolid";
import useThemeToken from "@UI/hooks/useThemeToken";

export interface SuggestionBadgeProps extends Omit<TagProps, "children"> {
  children: ReactNode;
}

/**
 * Shared id: several badges can render on one screen, and every definition
 * below is identical, so the duplicate ids all resolve to the same paint.
 */
const SPARKLE_GRADIENT_ID = "rtSuggestionBadgeSparkleGradient";

/**
 * Pill used to flag an AI-suggested/auto-filled value ("Assistant suggests",
 * "Auto-filled from your description"). No matching color token exists for #FFE4DC
 * yet, so it's a literal hex value here rather than a theme reference.
 */
export const SuggestionBadge: FC<SuggestionBadgeProps> = ({
  children,
  ...props
}) => {
  // Destructured: `useThemeToken` returns string[], and `stopColor` needs a
  // scalar.
  const [pink500, orange500] = useThemeToken("colors", [
    "pink.500",
    "orange.500",
  ]);

  return (
    <Tag
      borderRadius="8px"
      bg="#FFE4DC"
      color="gray.700"
      textAlign="center"
      fontSize="xs"
      fontWeight="400"
      lineHeight="150%"
      gap="4px"
      sx={{ backdropFilter: "blur(2px)" }}
      {...props}
    >
      {/* Gradient on an SVG path needs a paint server — `bgClip: text` works on
          text, not on a path's fill. Same trick as `PrefillBanner`. */}
      <svg
        width="0"
        height="0"
        aria-hidden
        focusable="false"
        style={{ position: "absolute", width: 0, height: 0 }}
      >
        <defs>
          <linearGradient
            id={SPARKLE_GRADIENT_ID}
            x1="45%"
            y1="0%"
            x2="55%"
            y2="100%"
          >
            <stop offset="0%" stopColor={pink500} />
            <stop offset="99.871%" stopColor={orange500} />
          </linearGradient>
        </defs>
      </svg>
      <Icon
        as={SparklesSolid}
        width="12px"
        height="12px"
        flexShrink={0}
        aria-hidden
        sx={{ path: { fill: `url(#${SPARKLE_GRADIENT_ID})` } }}
      />
      {children}
    </Tag>
  );
};
